import * as React from 'react'
import * as Moment from 'moment'
import { extendMoment } from 'moment-range'
import { DepartmentShape, OverviewUserShape, ResourcesOverviewCellDataShape } from '../../api/api_data_types'
import UserRow from './UserRow'
import DatesTableHeaderRows from './DatesTableHeaderRows'
import ExternalInstallerPopup from './ExternalInstallerPopup'
import CellTagPopup from './CellTagPopup'

const moment = extendMoment(Moment)

type OverviewDepartment = {
  id: number
  title: string
  users: Record<string, OverviewUserShape>
}

type ResourcesOverviewData = {
  data: Record<string, Record<string, ResourcesOverviewCellDataShape>>
  departments: Record<string, OverviewDepartment>
  holidays: Record<string, string>
}

type Installer = {
  id: number
  name: string
  email: string
}

type TableProps = {
  startDate: string
  endDate: string
  data: ResourcesOverviewData
  showWeekends: boolean
  showNormTime: boolean
  availableDepartments: DepartmentShape[]
  currentUser: { id: number; is_department_manager?: boolean }
}

type PopupState = {
  open: boolean
  mode: 'create' | 'edit'
  departmentId: number | null
  departmentTitle: string
  installer: Installer | null
}

type CellTagPopupState = {
  open: boolean
  user: OverviewUserShape | null
  date: string | null
  tag: {
    id: number
    name: string
    color: string
  } | null
  externalInstallerTime: number | null
}

type PendingCellTagState = {
  username: string
  date: string
} | null

export default function Table({
  startDate,
  endDate,
  data,
  showWeekends,
  showNormTime,
  availableDepartments,
  currentUser,
}: TableProps) {
  const start = moment(startDate)
  const end = moment(endDate)
  const usersData = data.data
  const { departments, holidays } = data
  const [popupState, setPopupState] = React.useState<PopupState>({
    open: false,
    mode: 'create',
    departmentId: null,
    departmentTitle: '',
    installer: null,
  })
  const [cellTagPopupState, setCellTagPopupState] = React.useState<CellTagPopupState>({
    open: false,
    user: null,
    date: null,
    tag: null,
    externalInstallerTime: null,
  })
  const [pendingCellTag, setPendingCellTag] = React.useState<PendingCellTagState>(null)

  const departmentsById = React.useMemo<Record<number, DepartmentShape>>(() => {
    return (availableDepartments || []).reduce(
      (memo, department) => {
        memo[department.id] = department
        return memo
      },
      {} as Record<number, DepartmentShape>
    )
  }, [availableDepartments])

  const canManageDepartment = (departmentId: number) => {
    const department = departmentsById[departmentId]
    if (!department || !department.external_installers_department) return false

    const managerIds = (department.managers || []).map((manager) => manager.id)
    return managerIds.includes(currentUser.id)
  }

  const openCreatePopup = (departmentId: number, departmentTitle: string) => {
    setPopupState({
      open: true,
      mode: 'create',
      departmentId,
      departmentTitle,
      installer: null,
    })
  }

  const openEditPopup = (departmentId: number, departmentTitle: string, installer: OverviewUserShape) => {
    setPopupState({
      open: true,
      mode: 'edit',
      departmentId,
      departmentTitle,
      installer: {
        id: installer.id,
        name: installer.name,
        email: installer.email,
      },
    })
  }

  const closePopup = () => {
    setPopupState((prev) => ({ ...prev, open: false }))
  }

  const openCellTagPopup = (user: OverviewUserShape, date: string, tag: CellTagPopupState['tag'], externalInstallerTime: number | null) => {
    setCellTagPopupState({
      open: true,
      user,
      date,
      tag,
      externalInstallerTime,
    })
  }

  const closeCellTagPopup = () => {
    setCellTagPopupState((prev) => ({ ...prev, open: false }))
  }

  const markCellTagMutationStart = () => {
    if (!cellTagPopupState.user || !cellTagPopupState.date) return

    setPendingCellTag({
      username: cellTagPopupState.user.username,
      date: cellTagPopupState.date,
    })
  }

  const markCellTagMutationEnd = () => {
    setPendingCellTag(null)
  }

  const isHolidayOrWeekend = (date: Moment.Moment) => {
    const isHoliday = Boolean(holidays[date.format('YYYY-MM-DD')])
    return isHoliday || date.weekday() === 0 || date.weekday() === 6
  }

  let range = Array.from(moment().range(start, end).by('day')) as Moment.Moment[]
  if (showWeekends === false) {
    range = range.filter((date) => !isHolidayOrWeekend(date))
  }

  return (
    <>
      <table className="table table-bordered">
        <thead>
          <DatesTableHeaderRows range={range} isHolidayOrWeekend={isHolidayOrWeekend} colSpanMultiplier={2} />
        </thead>
        <tbody>
          {Object.entries(departments).map(([departmentId, department]) => {
            const canManageExternalInstallers = canManageDepartment(department.id)
            return (
              <React.Fragment key={departmentId}>
                <tr className="resources-overview-department-title">
                  <td colSpan={range.length * 2 + 1}>
                    <div className="d-flex align-items-center">
                      <span>{department.title}</span>
                      {canManageExternalInstallers && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm ml-2"
                          onClick={() => openCreatePopup(department.id, department.title)}
                        >
                          + Tilføj montør
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {Object.entries(department.users).map(([username, user]) => {
                  if (!usersData[username]) return null
                  return (
                    <UserRow
                      key={username}
                      range={range}
                      userData={usersData[username]}
                      isHolidayOrWeekend={isHolidayOrWeekend}
                      user={user}
                      showNormTime={showNormTime}
                      canManageExternalInstallers={canManageExternalInstallers}
                      onExternalInstallerClick={() => openEditPopup(department.id, department.title, user)}
                      canManageCellTags={Boolean(currentUser.is_department_manager)}
                      onCellTagClick={({ date, tag, externalInstallerTime }) => openCellTagPopup(user, date, tag, externalInstallerTime)}
                      pendingCellTagDate={pendingCellTag?.username === user.username ? pendingCellTag.date : null}
                    />
                  )
                })}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
      <ExternalInstallerPopup
        open={popupState.open}
        mode={popupState.mode}
        departmentId={popupState.departmentId}
        departmentTitle={popupState.departmentTitle}
        installer={popupState.installer}
        onClose={closePopup}
      />
      <CellTagPopup
        open={cellTagPopupState.open}
        user={cellTagPopupState.user}
        date={cellTagPopupState.date}
        tag={cellTagPopupState.tag}
        externalInstallerTime={cellTagPopupState.externalInstallerTime}
        onClose={closeCellTagPopup}
        onMutationStart={markCellTagMutationStart}
        onMutationEnd={markCellTagMutationEnd}
      />
    </>
  )
}
